// useIsMobile (M-F).
//
// Single source of truth for "is the current viewport narrow enough that
// Tradeatory should switch to its desktop-first read-only mode?".
//
// Threshold matches Tailwind's `md` breakpoint (768px). At and above we
// assume a real desktop / tablet-landscape and render the full Builder.
// Below, the Strategy Builder canvas intercepts with a desktop-only
// notice and the sidebar collapses into a hamburger-triggered drawer.
//
// Implementation note: window.matchMedia is cheap to subscribe to and
// fires once per breakpoint crossing — far less spammy than a resize
// listener. SSR-safe via a default of `false` until the effect runs.

import { useEffect, useState } from 'react';

const MOBILE_QUERY = '(max-width: 767px)';

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Modern Safari supports addEventListener; older browsers need
    // addListener. The cast handles the legacy fallback cleanly.
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    const legacy = mql as unknown as { addListener: (cb: (e: MediaQueryListEvent) => void) => void; removeListener: (cb: (e: MediaQueryListEvent) => void) => void };
    legacy.addListener(handler);
    return () => legacy.removeListener(handler);
  }, []);

  return isMobile;
}
